import { z } from 'zod';
import { AuthMethod, Role } from '../enums';
import { PERMISSIONS } from '../permissions';

export const UserDto = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string(),
  roles: z.array(Role),
  authMethods: z.array(AuthMethod),
  active: z.boolean(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
});
export type UserDto = z.infer<typeof UserDto>;

export const InviteUserInput = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email(),
  displayName: z.string().trim().min(1).max(200).optional(),
  roles: z.array(Role).min(1),
});
export type InviteUserInput = z.infer<typeof InviteUserInput>;

export const UpdateUserInput = z
  .object({
    roles: z.array(Role).optional(),
    authMethods: z.array(AuthMethod).min(1, 'Choose at least one sign-in method').optional(),
    active: z.boolean().optional(),
    displayName: z.string().trim().min(1).max(200).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateUserInput = z.infer<typeof UpdateUserInput>;

export const MeDto = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string(),
  roles: z.array(Role),
  permissions: z.array(z.enum(PERMISSIONS)),
});
export type MeDto = z.infer<typeof MeDto>;

export const AuthConfigDto = z.object({
  googleEnabled: z.boolean(),
  emailCodeEnabled: z.boolean(),
  devLoginEnabled: z.boolean(),
});
export type AuthConfigDto = z.infer<typeof AuthConfigDto>;

export const DevLoginInput = z.object({
  email: z.string().trim().toLowerCase().email(),
});
export type DevLoginInput = z.infer<typeof DevLoginInput>;

export const LOGIN_CODE_TTL_MINUTES = 15;
export const LOGIN_CODE_MAX_ATTEMPTS = 5;
export const LOGIN_CODE_WINDOW_MINUTES = 15;
export const LOGIN_CODE_MAX_PER_EMAIL = 3;
export const LOGIN_CODE_MAX_PER_IP = 20;

const loginEmail = z.string().trim().toLowerCase().email().max(254);

export const EmailCodeRequestInput = z.object({ email: loginEmail });
export type EmailCodeRequestInput = z.infer<typeof EmailCodeRequestInput>;

export const EmailCodeVerifyInput = z.object({
  email: loginEmail,
  code: z
    .string()
    .transform((v) => v.replace(/\s+/g, ''))
    .pipe(z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from the email')),
});
export type EmailCodeVerifyInput = z.infer<typeof EmailCodeVerifyInput>;

export const EMAIL_CODE_REJECTIONS = ['code_invalid', 'code_expired', 'code_used', 'code_locked', 'inactive', 'method_not_allowed'] as const;
export type EmailCodeRejection = (typeof EMAIL_CODE_REJECTIONS)[number];
